import { useCallback, useEffect, useRef, useState } from "react";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import { isRetryableError } from "@/config/retryPolicy";
import { TERMINAL_STATUSES } from "@/constants/documentStatus";

/**
 * Response shape from GET /documents/{id}/classification — mirrors the Go
 * `DocumentClassificationResponse` in apps/mgmt-api/cmd/handlers/document_classification.go.
 *
 * - `auto_classified` is what the deterministic classifier picked at upload
 *   (read from the matched template's source_document_type).
 * - `override` is the reviewer's deliberate correction. nil/null when no
 *   override is set.
 * - `effective` is the value that takes effect on the next reprocess —
 *   `override ?? auto_classified`. Both can be null when the document
 *   hasn't produced a transformation yet.
 * - `available_options` is the list of enum values the dropdown should
 *   render — already filtered server-side by record_type so the reviewer
 *   only sees compatible shapes plus `other`. `unknown_format` is
 *   intentionally excluded; it's a classifier-only signal.
 * - `existing_template_options` is the subset of `available_options` that
 *   already have an active template for this document's (partner, vendor).
 *   Used by the override UI to flag which choices reuse an existing
 *   template vs trigger fresh AI inference on next reprocess.
 */
export interface DocumentClassification {
  auto_classified: string | null;
  override: string | null;
  effective: string | null;
  available_options: string[];
  existing_template_options: string[];
}

interface UseDocumentClassificationProps {
  documentId: string | undefined;
  /** Only fire when the document is in a reviewable state (e.g. transformation exists). */
  enabled: boolean;
  /**
   * Callback fired when the post-override reprocess settles back to
   * `review_required`. Consumers wire this to react-admin's useRefresh
   * (or equivalent) so the document, projection, field mappings, etc.
   * all re-fetch without the reviewer needing to refresh manually.
   *
   * Optional — when omitted, the consumer can read `isReprocessing`
   * from the return value and react however they want.
   */
  onReprocessComplete?: () => void;
}

interface UseDocumentClassificationReturn {
  classification: DocumentClassification | null;
  isLoading: boolean;
  error: string | null;
  /** Force a refetch — typically called after a successful override. */
  refetch: () => void;
  /** Persist a reviewer override. Pass `null` to clear an existing override. */
  setOverride: (value: string | null) => Promise<void>;
  /** True while a setOverride call is in flight. */
  isSaving: boolean;
  /**
   * True from when an override POST resolves until the worker's reprocess
   * settles the document back to `review_required` (or hits a terminal
   * non-review state, or polling times out). UI uses this to show a
   * loading indicator on the chip and disable further interactions.
   */
  isReprocessing: boolean;
  /**
   * Track a reprocess the CALLER has already dispatched (#6920): flips
   * `isReprocessing`, polls until the document settles, then refetches and
   * fires `onReprocessComplete`.
   *
   * Exposed so the review screen's Reprocess button gets the same progress and
   * settle-then-refresh behaviour as a classification override, instead of a
   * second copy of the poll that could drift from this one.
   */
  trackReprocess: () => void;
}

/**
 * Reads + writes the document's classification value (AB#4363).
 *
 * Uses httpClient directly rather than dataProvider.create / .getOne because
 * the endpoint isn't a CRUD resource — it's a custom action on the document.
 * Mirrors the pattern in useReviewProjection.
 */
// REPROCESS_POLL_INTERVAL_MS is how often we check the document status
// during a post-override reprocess. 2 seconds is responsive enough for the
// reviewer to feel "this is happening" without hammering the API.
const REPROCESS_POLL_INTERVAL_MS = 2000;

// REPROCESS_POLL_TIMEOUT_MS is the upper bound we wait for reprocess to
// settle before giving up. Sized for the typical 10-30s reprocess window
// with comfortable headroom. On timeout we stop polling, clear the loading
// state AND surface an unconfirmed-status message (#3804) — the override IS
// persisted, so the wording must not imply the change was lost, but staying
// silent made a timeout read as success.
const REPROCESS_POLL_TIMEOUT_MS = 90_000;

// REPROCESS_MAX_CONSECUTIVE_POLL_ERRORS caps how many CONSECUTIVE transient
// poll failures we absorb before giving up (#3804). Without a cap, a sustained
// 5xx spends the full 90s timeout reissuing the same GET every 2 seconds — 45
// pointless requests. Mirrors CASCADE_MAX_CONSECUTIVE_POLL_ERRORS. Any
// successful poll resets the streak; non-retryable statuses skip the budget
// entirely and abort on the first error.
const REPROCESS_MAX_CONSECUTIVE_POLL_ERRORS = 3;

/**
 * Message shown when the reprocess poll gives up. The override itself IS
 * persisted at this point — only the confirmation failed — so the wording must
 * not imply the change was lost (#3804).
 */
const REPROCESS_UNCONFIRMED_MESSAGE = "Couldn't confirm the reprocess finished. Refresh to check the current status.";

const reprocessPollErrorMessage = (err: unknown): string =>
  err instanceof Error && err.message
    ? `Couldn't confirm the reprocess finished: ${err.message}`
    : REPROCESS_UNCONFIRMED_MESSAGE;

// Document statuses that mean reprocess has settled. We delegate to the
// canonical TERMINAL_STATUSES set in @/constants/documentStatus so future
// status additions automatically extend the poll-stop conditions instead
// of silently leaving us hanging on a new terminal state. The set covers
// review_required (happy path), the various failure terminals, and the
// success/user-action terminals (completed/archived/deleted/rejected) —
// the latter are unlikely mid-reprocess but harmless to recognize.
const REPROCESS_TERMINAL_STATUSES = new Set<string>(TERMINAL_STATUSES);

export const useDocumentClassification = ({
  documentId,
  enabled,
  onReprocessComplete,
}: UseDocumentClassificationProps): UseDocumentClassificationReturn => {
  const [classification, setClassification] = useState<DocumentClassification | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Lifecycle anchor for the post-override poll. Holds the cancel flag
  // for whichever poll is currently in flight (or null when nothing's
  // running). Two cases this guards against:
  //
  //   1. Component unmount during reprocess — without this, the poll
  //      keeps running for up to 90 seconds after the user navigates
  //      away and eventually fires onReprocessComplete (→ useRefresh)
  //      against whatever page the user is now on.
  //   2. Back-to-back overrides — without this, a second setOverride
  //      starts a new poll without cancelling the prior one, so both
  //      run to completion and fire the callback twice.
  //
  // setOverride pre-cancels the previous poll, then stores the new poll's
  // cancel handle here. The unmount effect below cancels whatever's stored
  // on tear-down.
  const pollCancelRef = useRef<{ current: boolean } | null>(null);
  useEffect(() => {
    return () => {
      if (pollCancelRef.current) {
        pollCancelRef.current.current = true;
      }
    };
  }, []);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Fetch classification on mount + whenever documentId / refreshKey changes.
  useEffect(() => {
    if (!enabled || !documentId) {
      setClassification(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const url = `${getPartnerAwareApiUrl()}/documents/${documentId}/classification`;
    httpClient(url, { method: "GET" })
      .then(({ json }: { json: unknown }) => {
        if (cancelled) return;
        const payload = json as Partial<DocumentClassification> | undefined;
        if (!payload || !Array.isArray(payload.available_options)) {
          // Same diagnostic style as useReviewProjection — dev hint for the
          // common shape mismatch (route not registered / spec drift).
          console.error(
            "Classification endpoint returned an unexpected response shape. " +
              "Is the mgmt-api running with story/4362-document-classifier and the /classification route registered?",
            json
          );
          setError("Classification endpoint returned an unexpected response shape.");
          setClassification(null);
          return;
        }
        setClassification(payload as DocumentClassification);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("Document classification fetch failed:", err);
        setError(err instanceof Error ? err.message : "Failed to load classification");
        setClassification(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documentId, enabled, refreshKey]);

  // Poll the document status until reprocess settles. The override POST
  // triggers a server-side reprocess (status: review_required → reprocess
  // → processing → review_required), and we want the page to auto-refresh
  // when the doc lands back in a reviewable state — not force the reviewer
  // to manually refresh and guess when reprocess finished.
  //
  // Returns true when polling settled cleanly, false on timeout or error.
  // The `cancelled` ref lets a component unmount stop the poll early.
  const pollUntilReprocessSettles = useCallback(
    async (docID: string, cancelled: { current: boolean }): Promise<boolean> => {
      const start = Date.now();
      const url = `${getPartnerAwareApiUrl()}/documents/${docID}`;
      let consecutiveErrors = 0;
      while (!cancelled.current && Date.now() - start < REPROCESS_POLL_TIMEOUT_MS) {
        await new Promise((resolve) => setTimeout(resolve, REPROCESS_POLL_INTERVAL_MS));
        if (cancelled.current) return false;
        try {
          const { json } = (await httpClient(url, { method: "GET" })) as { json: unknown };
          const status = (json as { status?: unknown } | null)?.status;
          if (typeof status === "string" && REPROCESS_TERMINAL_STATUSES.has(status)) {
            return true;
          }
          consecutiveErrors = 0;
        } catch (err) {
          // #3804: this loop used to swallow EVERY error and reissue the GET
          // until the timeout, so a permanent 403/404 reproduced the very
          // retry-storm this ticket was filed about. A transient failure still
          // shouldn't fail the whole flow, but it now costs a bounded budget.
          //
          // Both terminal branches SET AN ERROR rather than just logging. The
          // caller only does `setIsReprocessing(false)` on a false return, so a
          // silent give-up stops the spinner and reads as success on an
          // operation that was denied — faster silence, not a surfaced error.
          // `retryUnauthorized` because this loop re-acquires a token on every
          // attempt (httpClient → getAccessToken) and has no checkError-driven
          // refresh of its own, so one 401 from a token blip should not be
          // terminal here.
          if (!isRetryableError(err, { retryUnauthorized: true })) {
            console.warn("Reprocess status poll failed permanently; giving up", err);
            setError(reprocessPollErrorMessage(err));
            return false;
          }
          consecutiveErrors += 1;
          if (consecutiveErrors >= REPROCESS_MAX_CONSECUTIVE_POLL_ERRORS) {
            console.warn("Reprocess status poll failed repeatedly; giving up", err);
            setError(reprocessPollErrorMessage(err));
            return false;
          }
          console.warn("Reprocess status poll failed; continuing", err);
        }
      }
      // Third terminal path: the 90s bound elapsed. Silent here would be the
      // same defect as the two catch-branches above — the caller only clears the
      // spinner, so an unconfirmed reprocess reads as success.
      //
      // Guarded on `cancelled` because the loop condition exits for BOTH reasons:
      // a cancelled poll (unmount, or a newer override superseding this one) must
      // not write an error, or it would surface against a poll the user replaced.
      if (!cancelled.current) {
        console.warn("Reprocess status poll timed out before the document settled");
        setError(REPROCESS_UNCONFIRMED_MESSAGE);
      }
      return false;
    },
    []
  );

  // Override write. Resolves when the POST returns; the post-POST reprocess
  // poll then runs in the background (isReprocessing flips to true while
  // it's running, false when it settles). The caller can either await this
  // promise and ignore the polling, or read isReprocessing and respond to
  // the auto-refresh callback.
  /**
   * Track a reprocess that has ALREADY been dispatched: flip isReprocessing,
   * poll until the document settles, then refetch the classification and tell
   * the consumer to refresh.
   *
   * Extracted from setOverride for #6920. The classification override was not
   * the only thing that triggers a reprocess — the review screen's Reprocess
   * button and the field-mapping save both do — and each of those needs the
   * same "show progress, then refresh the grid/projection/mappings" behaviour.
   * Duplicating the poll would have let them drift, and the drift would look
   * like "the page updates after a mapping save but not after a reprocess".
   *
   * Does NOT dispatch anything itself: the caller owns the POST, this owns
   * knowing when it finished.
   */
  const trackReprocess = useCallback(() => {
    // No document, nothing to poll. Returns silently rather than throwing: the
    // caller has already dispatched its POST by this point, so a missing id
    // here is a wiring bug in the consumer, not a reviewer-visible error.
    if (!documentId) return;

    // Cancel any prior poll first — back-to-back reprocesses must NOT
    // leave two pollers running side by side. Then publish this poll's
    // cancel handle on pollCancelRef so unmount and later calls find it.
    if (pollCancelRef.current) {
      pollCancelRef.current.current = true;
    }
    const cancelled = { current: false };
    pollCancelRef.current = cancelled;

    setIsReprocessing(true);
    void (async () => {
      const settled = await pollUntilReprocessSettles(documentId, cancelled);
      // Bail out of all post-poll side effects if this poll was cancelled
      // (component unmounted, or a newer override superseded). Without
      // this guard the cancelled poll could still race a state setter
      // against a fresh poll that's already taken over.
      if (cancelled.current) return;
      setIsReprocessing(false);
      if (settled) {
        // Refetch our own classification + tell the consumer to refresh
        // their resources (document, projection, etc.). Both run because
        // the consumer's refresh may not catch the classification field.
        refetch();
        if (onReprocessComplete) {
          onReprocessComplete();
        }
      }
    })();
  }, [documentId, refetch, onReprocessComplete, pollUntilReprocessSettles]);

  const setOverride = useCallback(
    async (value: string | null): Promise<void> => {
      if (!documentId) {
        throw new Error("Cannot set classification override without documentId");
      }
      setIsSaving(true);
      setError(null);
      try {
        const url = `${getPartnerAwareApiUrl()}/documents/${documentId}/classification`;
        const { json } = (await httpClient(url, {
          method: "POST",
          body: JSON.stringify({ source_document_type: value }),
        })) as { json: unknown };
        const payload = json as Partial<DocumentClassification> | undefined;
        if (payload && Array.isArray(payload.available_options)) {
          setClassification(payload as DocumentClassification);
        } else {
          // Fall back to refetch if the response shape is off — better than
          // silently leaving the UI showing stale state.
          refetch();
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to save classification";
        setError(message);
        // Surface to caller so the banner can react (e.g. revert dropdown).
        throw err instanceof Error ? err : new Error(message);
      } finally {
        setIsSaving(false);
      }

      // Hand progress tracking to trackReprocess (#6920): the same poll,
      // spinner and settle-then-refresh the reprocess button uses, so the two
      // entry points cannot drift.
      trackReprocess();
    },
    [documentId, trackReprocess]
  );

  return { classification, isLoading, error, refetch, setOverride, isSaving, isReprocessing, trackReprocess };
};
